/* テーマエディタの section 再描画で script が再評価されても、トップレベル宣言を global に残さない */
(() => {
  /* contract 未評価時の Theme Editor 再評価でも待てるよう、contract.js と同じ ready event 名を fallback する */
  const DEFAULT_SYNC_READY_EVENT = 'aureme:main-visual-sync:ready';

  function getMainVisualSync() {
    const Sync = window.AuremeMainVisual?.Sync;
    return Sync?.isReady?.(Sync) ? Sync : null;
  }

  function getMainVisualSyncReadyEventName() {
    return window.AuremeMainVisual?.Sync?.readyEventName || DEFAULT_SYNC_READY_EVENT;
  }

  function listenMainVisualBreakpointChange(breakpointQuery, callback) {
    if (breakpointQuery.addEventListener) {
      breakpointQuery.addEventListener('change', callback);
      return () => breakpointQuery.removeEventListener('change', callback);
    }

    breakpointQuery.addListener(callback);
    return () => breakpointQuery.removeListener(callback);
  }

  function listenMainVisualThemeEditorBlocks(isDesignMode, handleBlockSelect, handleBlockDeselect) {
    if (!isDesignMode) return null;

    document.addEventListener('shopify:block:select', handleBlockSelect);
    document.addEventListener('shopify:block:deselect', handleBlockDeselect);

    return () => {
      document.removeEventListener('shopify:block:select', handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', handleBlockDeselect);
    };
  }

  function listenMainVisualPageResume(callback) {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      callback();
    };

    window.addEventListener('pageshow', callback);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', callback);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }

  function defineAuremeMainVisual(Sync) {
    if (!Sync || customElements.get('aureme-main-visual')) return;

    const {
      ActiveMediaSync,
      MainVisualSliderSync,
      MainVisualUnderlayReadySync,
      constants,
      normalizeMainVisualAutoplayDelay,
      normalizeMainVisualSliderEffect,
    } = Sync;
    const { DESKTOP_VIEWPORT_NAME, MOBILE_VIEWPORT_NAME } = constants;

    function readMainVisualConfig(element) {
      const breakpointQuery = window.matchMedia('(min-width: 750px)');

      return {
        breakpointQuery,
        sectionElement: element.closest('.aureme-main-visual'),
        isDesignMode:
          element.hasAttribute('data-design-mode') || window.Shopify?.designMode === true,
        headerSection: element.hasAttribute('data-sync-header-underlay')
          ? document.querySelector('.section-header')
          : null,
        autoplayDelay: normalizeMainVisualAutoplayDelay(element.dataset.autoplayDelay),
        slideEffect: normalizeMainVisualSliderEffect(element.dataset.auremeMainVisualSlideEffect),
        underlayColorSources: {
          [MOBILE_VIEWPORT_NAME]: element.dataset.mobileUnderlayColorSource || '',
          [DESKTOP_VIEWPORT_NAME]: element.dataset.desktopUnderlayColorSource || '',
        },
      };
    }

    /* custom element は breakpoint 変化の検知と controller 間の受け渡しだけに責務を絞る */
    class AuremeMainVisual extends HTMLElement {
      constructor() {
        super();
        this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
        this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
        this.handleSyncRequest = this.handleSyncRequest.bind(this);
        this.handleDeferredSyncRequest = this.handleDeferredSyncRequest.bind(this);
        this.handlePageResume = this.handlePageResume.bind(this);
      }

      connectedCallback() {
        const {
          breakpointQuery,
          sectionElement,
          isDesignMode,
          headerSection,
          autoplayDelay,
          slideEffect,
          underlayColorSources,
        } = readMainVisualConfig(this);

        this.breakpointQuery = breakpointQuery;
        this.sectionElement = sectionElement;
        this.syncAnimationFrame = null;
        this.sliderSync = new MainVisualSliderSync(
          this,
          breakpointQuery,
          autoplayDelay,
          slideEffect,
        );
        this.activeMediaSync = new ActiveMediaSync(this, sectionElement, breakpointQuery);
        this.underlayReadySync = new MainVisualUnderlayReadySync({
          sectionElement,
          headerSection,
          underlayColorSources,
          requestSync: () => this.requestSync(),
        });
        this.eventCleanups = [
          listenMainVisualBreakpointChange(breakpointQuery, this.handleBreakpointChange),
          listenMainVisualPageResume(this.handlePageResume),
          listenMainVisualThemeEditorBlocks(
            isDesignMode,
            this.handleBlockSelect,
            this.handleBlockDeselect,
          ),
        ].filter(Boolean);

        this.underlayReadySync.connect();
        this.requestSync();
      }

      disconnectedCallback() {
        if (!this.breakpointQuery) return;

        for (const cleanup of this.eventCleanups || []) {
          cleanup();
        }
        this.eventCleanups = [];

        this.underlayReadySync?.disconnect();
        this.sliderSync?.disconnect();
        this.activeMediaSync?.disconnect();
        this.cancelSyncRequest();
      }

      handleBreakpointChange() {
        this.underlayReadySync?.reset();
        this.requestSync();
      }

      handlePageResume() {
        this.requestSync();
      }

      handleBlockSelect(event) {
        if (!this.isEventForCurrentSection(event)) return;

        const blockId = event.detail?.blockId;
        if (!blockId) return;

        this.sliderSync?.selectBlock(blockId);
      }

      handleBlockDeselect(event) {
        if (!this.isEventForCurrentSection(event)) return;

        this.sliderSync?.resumeActiveSlider();
      }

      isEventForCurrentSection(event) {
        const sectionId = event.detail?.sectionId;
        return !sectionId || sectionId === this.sectionElement?.dataset.sectionId;
      }

      /*
       * 複数の同期要求を 1 frame にまとめ、
       * slider・active media・underlay が同じ DOM 状態を見て同期できるようにする。
       */
      requestSync() {
        if (this.syncAnimationFrame) return;

        this.syncAnimationFrame = window.requestAnimationFrame(this.handleDeferredSyncRequest);
      }

      cancelSyncRequest() {
        if (!this.syncAnimationFrame) return;

        window.cancelAnimationFrame(this.syncAnimationFrame);
        this.syncAnimationFrame = null;
      }

      handleDeferredSyncRequest() {
        this.syncAnimationFrame = null;
        this.handleSyncRequest();
      }

      handleSyncRequest() {
        if (!this.activeMediaSync) return;

        this.sliderSync?.sync();
        const activeMedia = this.activeMediaSync.sync();
        this.underlayReadySync?.sync(
          activeMedia,
          this.activeMediaSync.hasMediaForActiveBreakpoint(),
        );
      }
    }

    customElements.define('aureme-main-visual', AuremeMainVisual);
  }

  const Sync = getMainVisualSync();
  if (Sync) {
    defineAuremeMainVisual(Sync);
    return;
  }

  window.addEventListener(
    getMainVisualSyncReadyEventName(),
    () => {
      defineAuremeMainVisual(getMainVisualSync());
    },
    { once: true },
  );
})();
